import { DelegateMapEntry } from 'containers/Base/ActionDelegate';
import { Project } from 'data/terra/Project';
import { Action } from 'redux';
import { ThunkAction } from 'redux-thunk';
import TerraBff from 'services/apis/terraBff/TerraBff';
import { RootState } from 'store/reducer';
import { hasMoreData } from 'v2/util/pagination';
import { ProjectSelectorState } from '../reducer';

// PAGE_SIZE should be very big to fetch all project at one time.
export const PAGE_SIZE = 1000000;

enum ActionTypes {
  loadInit = 'projects/loadInit',
  loadInitSuccess = 'projects/loadInitSuccess',
  loadInitFailure = 'projects/loadInitFailure',
}

export interface FetchProjectsAction {
  type: ActionTypes;
  projects: Project[];
  hasMore: boolean;
  error?: string;
}

export const fetchProjects = (): ThunkAction<
  void,
  RootState,
  unknown,
  Action<string>
> => async (dispatch, getState) => {
  dispatch({ type: ActionTypes.loadInit });

  const currentOrganization = getState().organizationList.selectedOrganization;

  const result = await TerraBff.project.fetchProjects({
    organizationId: currentOrganization?.id,
    page: 1,
    pageSize: PAGE_SIZE,
  });
  if (result.isSuccess()) {
    const data = result.get()!.data;

    dispatch({
      type: ActionTypes.loadInitSuccess,
      projects: data.projects,
      hasMore: hasMoreData(data.pagination),
    } as FetchProjectsAction);
  } else {
    dispatch({
      type: ActionTypes.loadInitFailure,
      error: result.exception().message,
    } as FetchProjectsAction);
  }

  return result;
};

export const fetchProjectsDelegate: DelegateMapEntry<
  FetchProjectsAction,
  ProjectSelectorState
> = {
  [ActionTypes.loadInit]: (
    state: ProjectSelectorState,
    action: FetchProjectsAction
  ): ProjectSelectorState => {
    return {
      ...state,
      projects: [],
      hasInitialized: false,
      currentPage: 0,
      isLoadingInit: true,
      hasMore: true,
      isLoadingMore: false,
      projectsError: undefined,
    };
  },
  [ActionTypes.loadInitSuccess]: (
    state: ProjectSelectorState,
    action: FetchProjectsAction
  ): ProjectSelectorState => {
    return {
      ...state,
      projects: action.projects,
      hasInitialized: true,
      currentPage: 1,
      isLoadingInit: false,
      hasMore: action.hasMore,
    };
  },
  [ActionTypes.loadInitFailure]: (
    state: ProjectSelectorState,
    action: FetchProjectsAction
  ): ProjectSelectorState => {
    return {
      ...state,
      isLoadingInit: false,
      projectsError: action.error,
    };
  },
};
